import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { paths } from '@constants/paths';
import { useBundleBasket } from '@hooks/useBundleBasket';
import { useDetectMidJourneyLogin } from '@hooks/useDetectMidJourneyLogin';
import { useSyncState } from '@hooks/useSyncState';

import { useGetMobileTariffs } from '../hooks/useGetMobileTariffs';
import { useMobileForm } from '../hooks/useMobileForm';
import { useMobileSubmit } from '../hooks/useMobileSubmit';
import { useRemoveDialog } from '../hooks/useRemoveDialog';
import { useSyncSims } from '../hooks/useSyncSims';
import { useSimsStore } from '../store';
import { withQualifiedDiscounts } from '../utils';
import { MobileContext } from './Context';
import type { MobileContextState } from './types';

const MobileProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const [hasFormChanged, setHasFormChanged] = useSyncState<boolean>(
    'has-mobile-form-changed',
    false
  );
  const [isFirstPageView, setIsFirstPageView] = useState(true);
  const { sims, loadSims, clearSims } = useSimsStore();
  useSyncSims();
  const { loading: basketLoading, basketData } = useBundleBasket();

  const submittedSims = useMemo(
    () => basketData?.basket?.mobile?.sims ?? [],
    [basketData]
  );

  const { submitMobile, isSubmitting } = useMobileSubmit();
  // The query is driven by the raw store selection (ids only). Feeding it the
  // discount-enriched SIMs would make every discount refresh re-trigger it.
  const {
    loading: tariffsLoading,
    mobileTariffs: availableTariffs,
    discounts,
  } = useGetMobileTariffs(sims, isSubmitting);

  const selectedSims = useMemo(
    () => withQualifiedDiscounts(sims, discounts),
    [sims, discounts]
  );

  const {
    initialValues,
    mobileTariffs,
    formRef,
    handleSimSubmit,
    handleEditSim,
    setEditedSim,
  } = useMobileForm(selectedSims, availableTariffs, setHasFormChanged);

  const {
    isRemoveModalOpen,
    isRemoving: isRemovingSim,
    removeSimName,
    handleModalClose,
    handleRemove,
    handleMobileServiceRemoved,
    handleRemoveSimClick,
  } = useRemoveDialog(sims, setHasFormChanged, clearSims);

  useDetectMidJourneyLogin({ basketData, loading: basketLoading });

  // Drops unsaved SIM changes. Safe to do locally since adding, editing and
  // removing a single SIM only mutate the sims store; nothing is persisted
  // until `submitMobile` runs. The store is re-hydrated from the basket on the
  // next visit to the page, so the basket stays the source of truth.
  const discardChanges = () => {
    clearSims();
    setHasFormChanged(false);
  };

  const handleSubmitMobile = (onComplete: () => void) =>
    submitMobile(() => {
      setHasFormChanged(false);
      onComplete();
    });

  // once the basket is loaded and there are no currently selected sims,
  // navigate to the add path by default. There is a delay visible due to the
  // basket loading, but this is somewhat unavoidable currently. It could be
  // mitigated either by navigating directly via the bundle tile or when the
  // bundle itself is cached.
  useEffect(() => {
    const isLoading = basketLoading || tariffsLoading;
    const hasData =
      basketData && mobileTariffs && !basketLoading && !tariffsLoading;

    if (hasData && !isLoading && isFirstPageView) {
      if (submittedSims.length === 0) {
        navigate(`${paths.mobile}/sim/add`);
      } else {
        loadSims(submittedSims);
      }
      setIsFirstPageView(false);
    }
  }, [
    basketData,
    submittedSims,
    navigate,
    basketLoading,
    tariffsLoading,
    isFirstPageView,
    setIsFirstPageView,
    loadSims,
    mobileTariffs,
  ]);

  const state: MobileContextState = {
    mobileLoading: basketLoading || tariffsLoading,
    formRef,
    isRemoveDialogOpen: isRemoveModalOpen,
    isRemovingService: isRemovingSim,
    hasFormChanged,
    mobileTariffs,
    removedSimName: removeSimName,
    simInitialValues: initialValues,
    selectedSims,
    handleGoBack: () => setEditedSim(null),
    discardChanges,
    editSim: handleEditSim,
    removeSimDialogClose: handleModalClose,
    removeSim: handleRemoveSimClick,
    removeSimSubmit: handleRemove,
    removeService: handleMobileServiceRemoved,
    submitMobile: handleSubmitMobile,
    submitSim: handleSimSubmit,
  };
  return <MobileContext value={state}>{children}</MobileContext>;
};

export { MobileProvider };
